import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ProjectCard from '../../components/ui/ProjectCard';
import { useProjectsQuery } from '../../hooks/useProjectQuery';
import { useQuery } from '@tanstack/react-query';
import { activeCategoriesQuery } from '../../queries/contentQueries';
import { FilterChipsSkeleton, ProjectGridSkeleton } from '../../components/skeletons/PageSkeletons';
import { Project } from '../../firebase/projectService';
import { usePageTitle } from '../../hooks/usePageTitle';
import { isYouTubeUrl, getYouTubeVideoId, getMediaType, getYouTubeEmbedUrl } from '../../utils/mediaUtils';
import { 
  CodeBracketIcon,
  TagIcon,
  StarIcon as StarSolidIcon,
  EyeIcon,
  CalendarIcon,
  CheckBadgeIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';

const ProjectsPage: React.FC = () => {
  usePageTitle("Projects");
  
  const [filter, setFilter] = useState<string>('all');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const { data: projects = [], isLoading, error, refetch } = useProjectsQuery();

  const { data: dynamicCategories = [], isPending: categoriesLoading } = useQuery(activeCategoriesQuery);
  
  const filteredProjects = filter === 'all' 
    ? projects 
    : projects.filter(project => {
        // Check if filter matches any category in the new categories array
        if (project.categories && Array.isArray(project.categories)) {
          if (project.categories.includes(filter)) return true;
        }
        // Check legacy category field for backward compatibility
        if (project.category === filter) return true;
        // Check tags
        if (project.tags.includes(filter)) return true;
        return false;
      });

  // Dynamically generate filter buttons based on dynamic categories and project data
  const getUniqueFilterOptions = () => {
    // Start with dynamic categories from the category service (these have proper ordering and colors)
    const categoryOptions = dynamicCategories.map(category => ({
      label: category.name,
      value: category.name,
      color: category.color,
      type: 'category' as const
    }));

    return [
      { label: 'All', value: 'all', color: '#10B981', type: 'all' as const },
      ...categoryOptions
    ];
  };

  const filterButtons = getUniqueFilterOptions();

  return (
    <div className="container py-16 lg:pt-[var(--page-top)]">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="section-title text-center">My Projects</h1>
        <p className="text-gray-600 dark:text-gray-400 text-center max-w-2xl mx-auto mb-12">
          Explore my portfolio of full-stack applications. Each project demonstrates my expertise 
          across multiple technologies - from backend APIs to frontend interfaces. 
          Use the filters below to discover projects by category or technology.
        </p>

        {error && (
          <div className="mb-8 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md text-center">
            {error instanceof Error ? error.message : 'An error occurred while loading projects'}
            <button 
              onClick={() => refetch()}
              className="ml-2 underline"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Filter buttons */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categoriesLoading ? (
            <FilterChipsSkeleton />
          ) : (
            filterButtons.map((button) => (
              <button
                key={button.value}
                onClick={() => setFilter(button.value)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all transform hover:scale-105 flex items-center ${
                  filter === button.value
                    ? 'bg-primary-600 text-white shadow-lg'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {button.color && button.type !== 'all' && (
                  <div
                    className="w-3 h-3 rounded-full mr-2"
                    style={{ backgroundColor: button.color }}
                  ></div>
                )}
                {button.label}
              </button>
            ))
          )}
        </div>

        {isLoading ? (
          <ProjectGridSkeleton />
        ) : (
          <>
            {/* Projects grid */}
            <AnimatePresence>
              {projects.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-12"
                >
                  <p className="text-xl text-gray-600 dark:text-gray-400">
                    No projects available yet. Check back soon!
                  </p>
                </motion.div>
              ) : (
                <motion.div 
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                  layout
                >
                  {filteredProjects.map((project) => (
                    <motion.div
                      key={project.id}
                      layout
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                      onClick={() => setSelectedProject(project)}
                      className="cursor-pointer"
                    >
                      <ProjectCard
                        title={project.title}
                        description={project.description}
                        imageUrl={project.image}
                        tags={project.tags}
                      />
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            {filteredProjects.length === 0 && projects.length > 0 && (
              <p className="text-center text-gray-600 dark:text-gray-400 mt-8">
                No projects found with the selected filter.
              </p>
            )}
          </>
        )}
      </motion.div>

    {/* Project Detail Modal */}
    {selectedProject && (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            onClick={() => setSelectedProject(null)}
          />

          <div
            className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-2xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full sm:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute top-0 right-0 pt-4 pr-4">
              <button
                type="button"
                className="bg-white dark:bg-gray-800 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                onClick={() => setSelectedProject(null)}
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="w-full">
              {/* Project Header */}
              <div className="flex items-center space-x-6 mb-6">
                <div className="w-20 h-20 rounded-xl overflow-hidden">
                  <img
                    src={selectedProject.image}
                    alt={selectedProject.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <h3 className="text-3xl font-bold text-gray-900 dark:text-white">
                        {selectedProject.title}
                      </h3>
                      {selectedProject.featured && <StarSolidIcon className="h-8 w-8 text-yellow-400" />}
                    </div>
                    
                    {/* Project Type and Company Info */}
                    <div className="mb-2">
                      <div className="flex items-center space-x-3">
                        <span className="text-lg text-gray-600 dark:text-gray-400">
                          {selectedProject.categories && selectedProject.categories.length > 0 
                            ? selectedProject.categories.join(' • ') 
                            : selectedProject.category || 'Project'
                          }
                        </span>
                        
                        {/* Project Type Badge */}
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          selectedProject.projectType === 'company' 
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                            : selectedProject.projectType === 'freelance' 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : selectedProject.projectType === 'open-source'
                            ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                        }`}>
                          {selectedProject.projectType === 'company' && '🏢 Company Project'}
                          {selectedProject.projectType === 'freelance' && '💼 Freelance Project'}
                          {selectedProject.projectType === 'open-source' && '🌐 Open Source'}
                          {selectedProject.projectType === 'personal' && '👤 Personal Project'}
                          {!selectedProject.projectType && '👤 Personal Project'}
                        </span>
                      </div>
                      
                      {/* Company Information */}
                      {selectedProject.company && (
                        <div className="mt-2 flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                            {selectedProject.projectType === 'freelance' ? 'Client:' : 'Company:'}
                          </span>
                          {selectedProject.companyWebsite ? (
                            <a 
                              href={selectedProject.companyWebsite} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-sm font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200 transition-colors flex items-center space-x-1"
                            >
                              <span>{selectedProject.company}</span>
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </a>
                          ) : (
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">
                              {selectedProject.company}
                            </span>
                          )}
                          <div className="w-2 h-2 bg-green-500 rounded-full" title="Verified professional work"></div>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-6 text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center space-x-1">
                        <CalendarIcon className="h-5 w-5" />
                        <span>
                          {selectedProject.createdAt 
                            ? new Date(selectedProject.createdAt.toDate()).getFullYear()
                            : 'Recent'
                          }
                        </span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <CodeBracketIcon className="h-5 w-5" />
                        <span>{selectedProject.technologies.length} Technologies</span>
                      </div>
                      {selectedProject.gallery && selectedProject.gallery.length > 0 && (
                        <div className="flex items-center space-x-1">
                          <PhotoIcon className="h-5 w-5" />
                          <span>{selectedProject.gallery.length} Media</span>
                        </div>
                      )}
                    </div>
                  </div>
              </div>

              {/* Quick Actions */}
              <div className="flex space-x-4 mb-6">
                {selectedProject.link && (
                  <a
                    href={selectedProject.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-primary-600 hover:bg-primary-700 transition-colors"
                  >
                    <EyeIcon className="h-5 w-5 mr-2" />
                    View Live Demo
                  </a>
                )}
                {selectedProject.github && (
                  <a
                    href={selectedProject.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-6 py-3 border border-gray-300 dark:border-gray-600 text-base font-medium rounded-xl text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                  >
                    <CodeBracketIcon className="h-5 w-5 mr-2" />
                    Source Code
                  </a>
                )}
              </div>

              {/* Professional Context - For Company/Freelance Projects */}
              {(selectedProject.projectType === 'company' || selectedProject.projectType === 'freelance') && selectedProject.company && (
                <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h4 className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                      Professional Work Experience
                    </h4>
                  </div>
                                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                     <div>
                       <span className="font-medium text-blue-800 dark:text-blue-200">Work Type:</span>
                       <span className="ml-2 text-blue-700 dark:text-blue-300">
                         {selectedProject.projectType === 'company' 
                           ? `${selectedProject.employmentType ? 
                               selectedProject.employmentType.charAt(0).toUpperCase() + 
                               selectedProject.employmentType.slice(1).replace('-', '-') 
                               : 'Full-time'} Employment`
                           : 'Freelance Contract'}
                       </span>
                     </div>
                    <div>
                      <span className="font-medium text-blue-800 dark:text-blue-200">
                        {selectedProject.projectType === 'freelance' ? 'Client:' : 'Employer:'}
                      </span>
                      <span className="ml-2 text-blue-700 dark:text-blue-300">{selectedProject.company}</span>
                    </div>
                    {!selectedProject.github && !selectedProject.link && (
                      <div className="md:col-span-2">
                        <span className="font-medium text-blue-800 dark:text-blue-200">Note:</span>
                        <span className="ml-2 text-blue-700 dark:text-blue-300">
                          Source code and live demo may not be available due to confidentiality agreements and proprietary nature of the work.
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Description */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  Project Description
                </h4>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  {selectedProject.longDescription || selectedProject.description}
                </p>
              </div>

              {/* Grid Layout for Features, Technologies, etc. */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Key Features */}
                {selectedProject.features && selectedProject.features.length > 0 && (
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      ✨ Key Features
                    </h4>
                    <div className="space-y-4">
                      {selectedProject.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="border-l-4 border-primary-500 pl-4">
                          <h5 className="font-semibold text-primary-600 dark:text-primary-400 mb-2">
                            {feature.name}
                          </h5>
                          <ul className="space-y-1">
                            {feature.subFeatures.map((subFeature, subIndex) => (
                              <li key={subIndex} className="flex items-start space-x-2">
                                <CheckBadgeIcon className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                                <span className="text-gray-600 dark:text-gray-300 text-sm">{subFeature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Technologies Used */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    🚀 Technologies Used
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    {selectedProject.technologies.map((tech, index) => (
                      <div
                        key={index}
                        className="flex items-center space-x-2 p-2 rounded-lg bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300"
                      >
                        <CodeBracketIcon className="h-4 w-4" />
                        <span className="font-medium text-sm">{tech}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Categories */}
              {selectedProject.categories && selectedProject.categories.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    📁 Categories
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedProject.categories.map((category, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium"
                      >
                        {category}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Tags */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  🏷️ Tags
                </h4>
                <div className="flex flex-wrap gap-2">
                  {selectedProject.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-700 text-blue-700 dark:text-blue-300 text-sm font-medium"
                    >
                      <TagIcon className="h-3 w-3 mr-1" />
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Screenshots Gallery */}
              {selectedProject.gallery && selectedProject.gallery.length > 0 && (
                <div className="mt-8">
                  <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-6 text-center">
                    🎥 Project Gallery ({selectedProject.gallery.length})
                  </h4>
                                      <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-2xl p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {selectedProject.gallery.map((item, index) => {
                          const mediaType = getMediaType(item);
                          const isYouTube = mediaType === 'youtube';
                          const videoId = isYouTube ? getYouTubeVideoId(item) : null;
                          const isGif = mediaType === 'gif';
                          
                          return (
                            <div key={index} className="relative group">
                              {isYouTube && videoId ? (
                                // YouTube Video Display
                                <div className="relative mx-auto w-full max-w-sm">
                                  <div className="relative bg-black rounded-2xl overflow-hidden shadow-2xl" style={{ aspectRatio: '16/9' }}>
                                    <iframe
                                      src={getYouTubeEmbedUrl(videoId)}
                                      title={`${selectedProject.title} video ${index + 1}`}
                                      className="w-full h-full"
                                      frameBorder="0"
                                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                      allowFullScreen
                                    ></iframe>
                                  </div>
                                  
                                  {/* Video title/number */}
                                  <div className="text-center mt-3">
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200">
                                      📺 Video Demo {index + 1}
                                    </span>
                                  </div>
                                </div>
                              ) : (
                                // Mobile Frame for Images/GIFs
                                <div className="relative mx-auto w-64 h-auto bg-gray-900 dark:bg-gray-100 rounded-3xl p-3 shadow-2xl">
                                  {/* Phone notch/speaker */}
                                  <div className="absolute top-6 left-1/2 transform -translate-x-1/2 w-16 h-1 bg-gray-700 dark:bg-gray-300 rounded-full"></div>
                                  
                                  {/* Screen */}
                                  <div className="relative bg-black rounded-2xl overflow-hidden" style={{ aspectRatio: '9/16' }}>
                                    <img
                                      src={item}
                                      alt={`${selectedProject.title} screenshot ${index + 1}`}
                                      className="w-full h-full object-cover cursor-pointer transition-transform group-hover:scale-105"
                                      onError={(e) => {
                                        console.error(`Failed to load screenshot ${index + 1}:`, item);
                                        e.currentTarget.style.display = 'none';
                                      }}
                                      onLoad={() => {
                                        console.log(`Successfully loaded screenshot ${index + 1}:`, item);
                                      }}
                                      onClick={() => {
                                        window.open(item, '_blank');
                                      }}
                                    />
                                    
                                    {/* Overlay on hover */}
                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-white text-center">
                                        <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg px-4 py-2">
                                          <p className="text-sm font-medium">Click to view full size</p>
                                          <p className="text-xs opacity-80">Screenshot {index + 1}</p>
                                          {isGif && (
                                            <p className="text-xs opacity-80">🎬 Animated</p>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* Home indicator */}
                                  <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-gray-600 dark:bg-gray-400 rounded-full"></div>
                                </div>
                              )}
                              
                              {/* Screenshot title/number for images/GIFs */}
                              {!isYouTube && (
                                <div className="text-center mt-3">
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200">
                                    {isGif ? '🎬 ' : ''}Screenshot {index + 1}
                                  </span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    
                    {/* View all screenshots button if many */}
                    {selectedProject.gallery.length > 6 && (
                      <div className="text-center mt-6">
                        <button
                          onClick={() => {
                            selectedProject.gallery?.forEach((screenshot, index) => {
                              setTimeout(() => window.open(screenshot, '_blank'), index * 200);
                            });
                          }}
                                                      className="inline-flex items-center px-6 py-3 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                          >
                            🎥 View All Media
                          </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
  );
};

export default ProjectsPage; 